import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Ban, CheckCircle, ChevronDown, Check, Clock, Contrast, Send } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../utils/dateUtils';
import { Avatar, Badge, Button, Icon, KebabMenu, Popover, PulseDot } from '../ui';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  due_date?: string;
  client_id?: string;
  client_name?: string;
  website_id?: string;
  website_title?: string;
  assigned_to?: string;
  priority: number;
  created_at: string;
  updated_at: string;
  video_url?: string;
}

/* ---- status anatomy: real app statuses mapped to the 2026 visual system ---- */
const STATUS_ORDER = ['Scheduled', 'In Progress', 'Blocked', 'Submitted', 'Complete'];

const STATUS_META: Record<string, { icon: LucideIcon; color: string }> = {
  Scheduled: { icon: Clock, color: 'var(--status-info)' },
  'In Progress': { icon: Contrast, color: 'var(--status-warn)' },
  Blocked: { icon: Ban, color: 'var(--status-down)' },
  Submitted: { icon: Send, color: 'var(--status-up)' },
  Complete: { icon: CheckCircle, color: 'var(--status-idle)' },
};

const FALLBACK_META = { icon: Contrast, color: 'var(--status-idle)' };

function isPastDue(dueDate: string | undefined) {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

function isDueToday(dueDate: string | undefined) {
  if (!dueDate) return false;
  return formatDate(dueDate, 'yyyy-MM-dd') === formatDate(new Date(), 'yyyy-MM-dd');
}

/* ---- 5-state status pill: mono uppercase, status dot, ck-menu-pop dropdown ---- */
interface StatusPillProps {
  status: string;
  onChange?: (newStatus: string) => void;
  onOpenChange?: (open: boolean) => void;
}

function StatusPill({ status, onChange, onOpenChange }: StatusPillProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const meta = STATUS_META[status] || FALLBACK_META;

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        className="pressable"
        title="Change status"
        onClick={() => setOpenState(!open)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '7px',
          padding: '4px 10px',
          borderRadius: 'var(--radius-pill)',
          border: '1px solid ' + (open ? 'var(--border-strong)' : 'var(--border-hairline)'),
          background: open ? 'var(--surface-active)' : 'var(--surface-raised)',
          color: 'var(--text-body)',
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
          fontSize: '11.5px',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          transition: 'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
        {status}
        <ChevronDown
          size={9}
          style={{
            color: 'var(--text-faint)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-out)',
          }}
        />
      </button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpenState(false)}
        align="right"
        minWidth={172}
        role="menu"
      >
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              className="hover-wash pressable"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setOpenState(false);
                onChange?.(s);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                background: 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-body)',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: (STATUS_META[s] || FALLBACK_META).color,
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1 }}>{s}</span>
              {s === status ? <Check size={10} style={{ color: 'var(--text-faint)' }} /> : null}
            </button>
          ))}
      </Popover>
    </span>
  );
}

/* ---- one queue row: index/grip | status icon | main | right cluster ---- */
interface SortableTaskItemProps {
  task: Task;
  index: number;
  isLast: boolean;
  onClick: (task: Task) => void;
  showUserBadge: boolean;
  users: { id: string; name: string; email: string; role?: string }[];
  onStatusChange?: (taskId: string, newStatus: string) => void;
}

function SortableTaskItem({ task, index, isLast, onClick, showUserBadge, users, onStatusChange }: SortableTaskItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const [pillOpen, setPillOpen] = useState(false);

  const isDone = task.status === 'Complete';
  const isReview = task.status === 'Submitted';
  const pastDue = isPastDue(task.due_date) && !isDone && !isReview;
  const meta = STATUS_META[task.status] || FALLBACK_META;
  const StatusIcon = meta.icon;

  const assignedUser = users.find((u) => u.id === task.assigned_to);
  const assignedUserName = assignedUser ? assignedUser.name : 'Unassigned';

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: pillOpen ? 40 : 'auto',
    position: 'relative',
    alignItems: 'center',
    padding: '12px 16px 12px 8px',
    cursor: 'pointer',
    opacity: isDragging ? 0.35 : isDone ? 0.55 : 1,
    boxShadow: isReview ? 'inset 2px 0 0 var(--status-up)' : 'none',
    borderBottom: isLast ? 'none' : '1px solid var(--border-hairline)',
    borderRadius: isLast ? '0 0 var(--radius-lg) var(--radius-lg)' : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      // Phones: the status/assignee cluster drops to a second line under the title, so the title
      // gets the full width instead of ~12 characters
      className={`hover-wash ck-ff-row grid grid-cols-[24px_16px_minmax(0,1fr)] gap-x-[10px] gap-y-2 md:grid-cols-[40px_22px_1fr_auto] md:gap-[12px]${isReview ? ' ck-row-submitted' : ''}`}
      onClick={() => onClick(task)}
    >
      {/* priority index — crossfades to grip on hover */}
      <span style={{ position: 'relative', textAlign: 'center' }}>
        <span className="t-num ck-ff-idx" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        {/* No grip on phones: a touch drag on the row scrolls the page instead, and the grip
            painted over the index number */}
        <span
          className="ck-ff-grip hidden md:inline-flex"
          title="Drag to reorder"
          style={{
            position: 'absolute',
            inset: 0,
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-faint)',
            cursor: 'grab',
          }}
        >
          <Icon name="grip-vertical" size={12} />
        </span>
      </span>

      {/* status icon */}
      <StatusIcon size={14} strokeWidth={1.75} style={{ color: meta.color }} aria-hidden="true" />

      {/* main */}
      <span style={{ minWidth: 0 }}>
        <span className="flex-wrap md:flex-nowrap" style={{ display: 'flex', alignItems: 'center', gap: '4px 9px', minWidth: 0 }}>
          <span
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: isDone ? 'var(--text-muted)' : 'var(--text-body)',
              textDecoration: isDone ? 'line-through' : 'none',
              minWidth: 0,
              // a long URL or slug in a title would otherwise push the phone page sideways
              overflowWrap: 'anywhere',
            }}
            className="md:truncate"
          >
            {task.title}
          </span>
          {task.website_title ? (
            <Link
              to={`/private-network/${task.website_id}`}
              onClick={(e) => e.stopPropagation()}
              style={{ textDecoration: 'none', flexShrink: 0 }}
            >
              <Badge tone="up">{task.website_title}</Badge>
            </Link>
          ) : null}
        </span>
        <span
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '2px 8px',
            marginTop: '3px',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-faint)',
          }}
        >
          {task.client_name ? <span>{task.client_name}</span> : null}
          {task.due_date ? (
            <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
              <Clock size={9} aria-hidden="true" />
              {isDueToday(task.due_date) ? 'Due today' : `Due ${formatDate(task.due_date, 'MMM d')}`}
            </span>
          ) : null}
        </span>
      </span>

      {/* right cluster */}
      <span
        className="col-start-3 md:col-start-auto"
        style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 9px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {pastDue ? (
          <Badge tone="down" dot pulse>
            Past Due
          </Badge>
        ) : null}
        {showUserBadge ? (
          <span
            title={assignedUserName}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 9px 3px 4px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
            }}
          >
            <Avatar name={assignedUserName} size={17} />
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
              {assignedUserName.split(' ')[0]}
            </span>
          </span>
        ) : null}
        <StatusPill
          status={task.status}
          onChange={(s) => onStatusChange?.(task.id, s)}
          onOpenChange={setPillOpen}
        />
      </span>
    </div>
  );
}

interface FulfillmentTaskListProps {
  tasks: Task[];
  isLoading: boolean;
  isReordering: boolean;
  selectedUserName: string;
  selectedUserId: string | null;
  users: { id: string; name: string; email: string; role?: string }[];
  handleOpenTask: (task: Task) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleStatusChange?: (taskId: string, newStatus: string) => void;
  filterType: 'overdue' | 'specific-date' | 'all';
  selectedDate: string;
  /** Header ⋮ → Archive Submitted. The page owns the confirm + write. */
  onArchiveSubmitted?: () => void;
}

export default function FulfillmentTaskList({
  tasks,
  isLoading,
  isReordering,
  selectedUserName,
  selectedUserId,
  users,
  handleOpenTask,
  handleDragEnd,
  handleStatusChange,
  filterType,
  selectedDate,
  onArchiveSubmitted,
}: FulfillmentTaskListProps) {
  const submittedCount = tasks.filter((t) => t.status === 'Submitted').length;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require more dragging distance to activate
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Determine if we should show user badges (when "All Users" is selected)
  const showUserBadge = !selectedUserId || selectedUserId === 'all';

  // Dynamic header text based on filter type
  const getHeaderText = () => {
    const suffix = selectedUserName !== 'All Users' ? ` · ${selectedUserName}` : '';
    if (filterType === 'overdue') {
      return `Tasks Due Today & Before${suffix}`;
    } else if (filterType === 'specific-date') {
      const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });
      return `Tasks Due on ${formattedDate}${suffix}`;
    }
    return `All Tasks${suffix}`;
  };

  const emptyCopy =
    filterType === 'overdue'
      ? 'No tasks are due today or overdue.'
      : filterType === 'specific-date'
        ? 'No tasks are due on the selected date.'
        : selectedUserName === 'All Users'
          ? 'There are currently no assigned tasks from any user.'
          : `There are currently no tasks assigned to ${selectedUserName || 'this user'}.`;

  return (
    <div
      style={{
        marginBottom: 'var(--space-6)',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'visible',
      }}
    >
      {/* raised header band */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '13px 16px',
          background: 'var(--surface-raised)',
          borderBottom: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
        }}
      >
        <span className="t-eyebrow" style={{ color: 'var(--text-muted)', minWidth: 0 }}>
          {getHeaderText()}
        </span>
        <span
          className="t-eyebrow"
          style={{ color: 'var(--text-faint)', display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}
        >
          {isReordering ? (
            <>
              <PulseDot tone="info" /> Updating priorities...
            </>
          ) : (
            // Phones can't drag-reorder (a touch drag scrolls), so the hint only shows from md up
            <span className="hidden md:inline">Drag to reprioritize</span>
          )}
          {onArchiveSubmitted ? (
            <KebabMenu
              label="Task list actions"
              actions={[
                {
                  label: submittedCount > 0 ? `Archive Submitted (${submittedCount})` : 'Archive Submitted',
                  icon: faBoxArchive,
                  disabled: isLoading || submittedCount === 0,
                  onClick: onArchiveSubmitted,
                },
              ]}
            />
          ) : null}
        </span>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }}>
          <div
            className="animate-spin"
            style={{
              width: '28px',
              height: '28px',
              border: '3px solid var(--accent)',
              borderTopColor: 'transparent',
              borderRadius: '50%',
            }}
          />
        </div>
      ) : tasks.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '52px',
              height: '52px',
              borderRadius: '50%',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              marginBottom: '14px',
            }}
          >
            <CheckCircle size={20} strokeWidth={1.75} style={{ color: 'var(--status-up)' }} aria-hidden="true" />
          </span>
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            No Tasks Assigned
          </h3>
          <p style={{ margin: '0 0 18px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{emptyCopy}</p>
          <Button
            size="sm"
            icon="plus"
            onClick={() => {
              // Navigate to clients page
              window.location.href = '/clients';
            }}
          >
            Assign New Task
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tasks.map((task) => task.id)} strategy={verticalListSortingStrategy}>
            <div>
              {tasks.map((task, idx) => (
                <SortableTaskItem
                  key={task.id}
                  task={task}
                  index={idx}
                  isLast={idx === tasks.length - 1}
                  onClick={handleOpenTask}
                  showUserBadge={showUserBadge}
                  users={users}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
